'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { idbStorage } from '@/lib/idb-storage';
import type { LibraryConnection } from '@/lib/opds/types';

function genId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts (e.g. HTTP on LAN)
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The primary library — pre-seeded on first load, unremovable, URL is
 * fixed at build time. The user can only set / edit credentials on it.
 * All other libraries the user adds behave normally.
 *
 * URL is configurable at build time via NEXT_PUBLIC_DEFAULT_LIBRARY_URL
 * so the same source ships against different deployments without
 * requiring a code change.
 */
export const PRIMARY_LIBRARY_ID = 'sbflibrary-primary';
export const PRIMARY_LIBRARY_NAME = 'SBFLibrary';
export const PRIMARY_LIBRARY_URL =
  process.env.NEXT_PUBLIC_DEFAULT_LIBRARY_URL ?? 'https://sbflibrary.ddns.net/opds';

export function isPrimaryLibrary(id: string | null | undefined): boolean {
  return id === PRIMARY_LIBRARY_ID;
}

function buildPrimaryLibrary(): LibraryConnection {
  return {
    id: PRIMARY_LIBRARY_ID,
    name: PRIMARY_LIBRARY_NAME,
    rootUrl: PRIMARY_LIBRARY_URL,
    addedAt: 0,
  };
}

interface LibraryState {
  libraries: LibraryConnection[];
  addLibrary: (lib: Omit<LibraryConnection, 'id' | 'addedAt'>) => void;
  /**
   * Mutation. Refuses to change <c>rootUrl</c> on the primary library —
   * the URL is build-time fixed. <c>auth</c>, <c>name</c>, and other
   * fields can still be edited. Removing isPrimary is also blocked
   * implicitly (no caller mutates that field).
   */
  updateLibrary: (id: string, updates: Partial<LibraryConnection>) => void;
  /** No-op for the primary library — it's deliberately unremovable. */
  removeLibrary: (id: string) => void;
  getLibrary: (id: string) => LibraryConnection | undefined;
}

/**
 * persist version. Bumping wipes the on-disk libraries array and lets
 * <see cref="migrate"/> reseed with just the primary library — the
 * one-shot reset the user requested in 2026-05-09.
 */
const SCHEMA_VERSION = 2;

export const useLibraryStore = create<LibraryState>()(
  persist(
    (set, get) => ({
      libraries: [buildPrimaryLibrary()],
      addLibrary: (lib) =>
        set((state) => ({
          libraries: [
            ...state.libraries,
            { ...lib, id: genId(), addedAt: Date.now() },
          ],
        })),
      updateLibrary: (id, updates) =>
        set((state) => ({
          libraries: state.libraries.map((l) => {
            if (l.id !== id) return l;
            // Primary library: ignore rootUrl + id mutations. Auth and
            // name can still be set so the user can log in / re-label.
            if (isPrimaryLibrary(id)) {
              const { rootUrl, id: _id, ...allowed } = updates;
              void rootUrl; void _id;
              return { ...l, ...allowed };
            }
            return { ...l, ...updates };
          }),
        })),
      removeLibrary: (id) =>
        set((state) => {
          if (isPrimaryLibrary(id)) return state; // unremovable
          return { libraries: state.libraries.filter((l) => l.id !== id) };
        }),
      getLibrary: (id) => get().libraries.find((l) => l.id === id),
    }),
    {
      name: 'bookshelf-libraries',
      storage: createJSONStorage(() => idbStorage),
      version: SCHEMA_VERSION,
      // One-shot reset: wipe everything that was previously persisted
      // and reseed with just the primary library. The migration runs
      // once per device when the user first loads a build with
      // SCHEMA_VERSION >= 2; subsequent loads see the new version and
      // skip migration. User-added secondary libraries from later
      // sessions persist normally.
      migrate: () => ({ libraries: [buildPrimaryLibrary()] }),
      // After hydration, defensively re-inject the primary library if
      // it's missing (e.g. someone hand-edited IndexedDB) and force
      // its URL to the build-time const so a stale persisted value
      // can't pin the user to a dead domain.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const idx = state.libraries.findIndex((l) => l.id === PRIMARY_LIBRARY_ID);
        if (idx < 0) {
          state.libraries = [buildPrimaryLibrary(), ...state.libraries];
        } else if (state.libraries[idx].rootUrl !== PRIMARY_LIBRARY_URL) {
          state.libraries[idx] = {
            ...state.libraries[idx],
            rootUrl: PRIMARY_LIBRARY_URL,
          };
        }
      },
    }
  )
);
